/**
 * StudentViewModal — Модальное окно с деталями ученика.
 *
 * Показывает ФИО, дату рождения, школу, группы, центры, родителей.
 * Позволяет привязывать родителей через Select с поиском.
 */
import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal, Stack, Text, Badge, Group, Box, Divider, Table, Button, Select, ActionIcon,
} from '@mantine/core';
import {
  IconCalendarTime, IconBuilding, IconUser, IconPlus, IconTrash,
} from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import tokens from '../../theme/designTokens';
import { linkParent, unlinkParent } from '../../services/api/studentsService';
import { getParents } from '../../services/api/parentsService';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    timeZone: 'Europe/Moscow',
  });
};

const StudentViewModal = ({ opened, onClose, student, onDataChange }) => {
  const [addParentOpen, setAddParentOpen] = useState(false);
  const [parentOptions, setParentOptions] = useState([]);
  const [selectedParentId, setSelectedParentId] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);

  // Загрузка списка родителей для привязки
  const loadParents = useCallback(async () => {
    try {
      const data = await getParents({ pageSize: 100, isActive: true });
      const linkedIds = new Set((student?.parents || []).map((p) => p.id));
      setParentOptions(
        (data.items || [])
          .filter((p) => !linkedIds.has(p.id))
          .map((p) => {
            const name = [p.last_name, p.first_name].filter(Boolean).join(' ');
            const children = (p.students || []).map((s) => s.full_name || s.first_name).join(', ');
            return {
              value: String(p.id),
              label: name || p.email,
              description: children ? `Дети: ${children}` : 'Нет привязанных детей',
            };
          })
      );
    } catch (err) {
      console.error('Failed to load parents:', err);
    }
  }, [student?.parents]);

  useEffect(() => {
    if (addParentOpen) loadParents();
  }, [addParentOpen, loadParents]);

  const handleLinkParent = async () => {
    if (!selectedParentId || !student) return;
    setLinkLoading(true);
    try {
      await linkParent(student.id, parseInt(selectedParentId));
      notifications.show({ title: 'Успешно', message: 'Родитель добавлен', color: 'green' });
      setAddParentOpen(false);
      setSelectedParentId(null);
      if (onDataChange) onDataChange();
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    } finally {
      setLinkLoading(false);
    }
  };

  const handleUnlinkParent = (parent) => {
    modals.openConfirmModal({
      title: 'Удалить родителя',
      centered: true,
      children: <Text size="sm">Удалить «{parent.email}» от ученика «{student?.full_name || student?.first_name}»?</Text>,
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await unlinkParent(student.id, parent.id);
          notifications.show({ title: 'Успешно', message: 'Родитель удалён', color: 'green' });
          if (onDataChange) onDataChange();
        } catch (err) {
          notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
        }
      },
    });
  };

  if (!student) return null;

  const groups = student.study_groups || [];
  const organizations = student.organizations || [];
  const parents = student.parents || [];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <Text fw={600}>{student.full_name || student.first_name}</Text>
          <Badge size="sm" variant="filled" color={student.is_active ? 'green' : 'gray'} radius="xl">
            {student.is_active ? 'Активен' : 'Неактивен'}
          </Badge>
        </Group>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        {/* Основная информация */}
        <Group gap="lg" wrap="wrap">
          {student.date_of_birth && (
            <Box>
              <Text size="xs" c="dimmed">Дата рождения</Text>
              <Text size="sm" fw={500}>{formatDate(student.date_of_birth)}</Text>
            </Box>
          )}
          {student.school && (
            <Box>
              <Text size="xs" c="dimmed">Школа</Text>
              <Text size="sm" fw={500}>{student.school}</Text>
            </Box>
          )}
        </Group>

        {/* Центры */}
        {organizations.length > 0 && (
          <Box>
            <Text size="sm" fw={600} mb="xs">
              <Group gap={4}><IconBuilding size={16} /> Центры ({organizations.length})</Group>
            </Text>
            <Group gap="xs">
              {organizations.map((o) => (
                <Badge key={o.id} variant="filled" color="blue" radius="xl" size="md">{o.name}</Badge>
              ))}
            </Group>
          </Box>
        )}

        {/* Группы */}
        <Box>
          <Text size="sm" fw={600} mb="xs">
            <Group gap={4}><IconCalendarTime size={16} /> Группы ({groups.length})</Group>
          </Text>
          {groups.length > 0 ? (
            <Table striped highlightOnHover>
              <Table.Thead><Table.Tr><Table.Th>Группа</Table.Th><Table.Th>Центр</Table.Th></Table.Tr></Table.Thead>
              <Table.Tbody>
                {groups.map((g) => (
                  <Table.Tr key={g.id}>
                    <Table.Td><Badge size="sm" variant="filled" color="indigo" radius="xl">{g.name}</Badge></Table.Td>
                    <Table.Td><Text size="sm">{g.organization_name || '—'}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          ) : (
            <Text size="sm" c="dimmed" ta="center" py="sm">Не записан ни в одну группу</Text>
          )}
        </Box>

        <Divider />

        {/* Родители */}
        <Box>
          <Group justify="space-between" mb="sm">
            <Text size="sm" fw={600}>
              <Group gap={4}><IconUser size={16} /> Родители ({parents.length})</Group>
            </Text>
            <Button variant="light" size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAddParentOpen(true)}>
              Добавить
            </Button>
          </Group>

          {/* Форма привязки родителя */}
          {addParentOpen && (
            <Box mb="sm" p="sm" style={{
              borderRadius: 12,
              border: `1px solid ${tokens.colors.neutral[3]}`,
              backgroundColor: tokens.colors.neutral[1],
            }}>
              <Group gap="sm">
                <Select
                  placeholder="Поиск родителя по имени или email..."
                  data={parentOptions}
                  value={selectedParentId}
                  onChange={setSelectedParentId}
                  searchable
                  renderOption={({ option }) => (
                    <Box>
                      <Text size="sm" fw={500}>{option.label}</Text>
                      {option.description && <Text size="xs" c="dimmed">{option.description}</Text>}
                    </Box>
                  )}
                  style={{ flex: 1 }}
                  radius="xl"
                />
                <Button size="sm" onClick={handleLinkParent} loading={linkLoading} disabled={!selectedParentId}>
                  Добавить
                </Button>
                <Button variant="subtle" size="sm" color="gray" onClick={() => { setAddParentOpen(false); setSelectedParentId(null); }}>
                  Отмена
                </Button>
              </Group>
            </Box>
          )}

          {parents.length > 0 ? (
            <Stack gap="xs">
              {parents.map((p) => (
                <Group key={p.id} justify="space-between">
                  <Group gap="sm">
                    <Text size="sm" fw={500}>
                      {[p.last_name, p.first_name].filter(Boolean).join(' ') || p.email}
                    </Text>
                    {p.phone && <Text size="xs" c="dimmed">{p.phone}</Text>}
                    <Text size="xs" c="dimmed">{p.email}</Text>
                  </Group>
                  <ActionIcon variant="subtle" color="red" size="sm" onClick={() => handleUnlinkParent(p)}>
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed" ta="center" py="sm">Нет привязанных родителей</Text>
          )}
        </Box>

        {/* Заметки */}
        {student.notes && (
          <><Divider /><Box><Text size="xs" c="dimmed">Заметки</Text><Text size="sm">{student.notes}</Text></Box></>
        )}

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose}>Закрыть</Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default StudentViewModal;
